import { useState, useEffect, useRef } from 'react';

export default function MarketingSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="marketing"
      className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-10 w-32 h-32 bg-red-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-pink-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Data Driven 
            <span className="bg-gradient-to-r from-red-400 to-pink-500 bg-clip-text text-transparent"> Growth Marketing</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Intelligent strategies for measurable market impact. Transform data into decisive growth strategies.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <div className="space-y-8">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/20 hover:border-red-500/50 transition-all duration-500">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">Performance Analytics</h3>
                    <p className="text-gray-300 leading-relaxed">
                      Advanced data analysis and predictive intelligence to optimize every marketing decision and maximize ROI.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/20 hover:border-red-500/50 transition-all duration-500">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">AI-Powered Campaigns</h3>
                    <p className="text-gray-300 leading-relaxed">
                      Leverage artificial intelligence for automated optimization, audience targeting, and personalized content delivery.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/20 hover:border-red-500/50 transition-all duration-500">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-target-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">Precision Targeting</h3>
                    <p className="text-gray-300 leading-relaxed">
                      Reach the right audience with laser-focused targeting strategies based on behavioral data and market insights.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <a
                href="/services/marketing"
                className="group bg-gradient-to-r from-red-600 to-pink-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-red-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-red-500/25 whitespace-nowrap cursor-pointer inline-flex items-center"
              >
                Explore Data Driven Growth Marketing
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300"></i>
              </a>
            </div>
          </div>

          {/* Right Image */}
          <div className={`transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl blur-2xl opacity-20 animate-pulse"></div>
              <img 
                src="/assets/marketing-dashboard.jpg"
                alt="Data Driven Growth Marketing Dashboard"
                className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
